<script>
import { mapGetters } from 'vuex';
import { getUnixTime } from 'date-fns';
import {
  CheckmarkCircle02Icon,
  RepeatIcon,
  Tag01Icon,
  Timer02Icon,
  UserAdd01Icon,
} from '@hugeicons/core-free-icons';
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import LabelQuickActionsMenu from 'dashboard/components-next/label/LabelQuickActionsMenu.vue';
import LifecycleQuickActionsMenu from 'dashboard/components-next/lifecycle/LifecycleQuickActionsMenu.vue';
import SnoozeQuickActionsMenu from 'dashboard/components-next/snooze/SnoozeQuickActionsMenu.vue';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import AgentAssignmentList from 'dashboard/components-next/agent/AgentAssignmentList.vue';
import {
  buildAgentAssignmentSections,
  fetchAgentTeamData,
  UNASSIGNED_VALUE,
} from 'dashboard/helper/agentAssignmentMenu';

const MENU = {
  PRIORITY: 'priority',
  STATUS: 'status',
  SNOOZE: 'snooze',
  AGENT: 'agent',
  LABEL: 'label',
  LIFECYCLE: 'lifecycle',
};

export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    LabelQuickActionsMenu,
    LifecycleQuickActionsMenu,
    SnoozeQuickActionsMenu,
    AgentLoadingPlaceholder,
    AgentAssignmentList,
  },
  props: {
    status: {
      type: String,
      default: '',
    },
    inboxId: {
      type: Number,
      default: null,
    },
    contactId: {
      type: Number,
      default: null,
    },
    assignee: {
      type: Object,
      default: () => null,
    },
    lifecycleStageId: {
      type: [Number, String, null],
      default: null,
    },
    priority: {
      type: String,
      default: null,
    },
    conversationLabels: {
      type: Array,
      default: () => [],
    },
    allowedOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'updateConversation',
    'assignPriority',
    'assignAgent',
    'assignLabel',
    'removeLabel',
    'close',
  ],
  data() {
    return {
      MENU,
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.CLOSE'),
          hugeIcon: CheckmarkCircle02Icon,
        },
        {
          key: wootConstants.STATUS_TYPE.OPEN,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN'),
          icon: 'arrow-redo',
        },
        {
          key: wootConstants.STATUS_TYPE.PENDING,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.PENDING'),
          icon: 'book-clock',
        },
      ],
      snoozeOption: {
        key: wootConstants.STATUS_TYPE.SNOOZED,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE'),
        hugeIcon: Timer02Icon,
      },
      priorityConfig: {
        key: MENU.PRIORITY,
        label: this.$t('CONVERSATION.PRIORITY.TITLE'),
        icon: 'warning',
        options: [
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
            key: null,
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
            key: 'urgent',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
            key: 'high',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
            key: 'medium',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
            key: 'low',
          },
        ].filter(item => item.key !== this.priority),
      },
      labelMenuConfig: {
        key: MENU.LABEL,
        hugeIcon: Tag01Icon,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      agentMenuConfig: {
        key: MENU.AGENT,
        hugeIcon: UserAdd01Icon,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      lifecycleMenuConfig: {
        key: MENU.LIFECYCLE,
        hugeIcon: RepeatIcon,
        label: this.$t('CONTACTS_LAYOUT.TABLE.LIFECYCLE'),
      },
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      lifecycleStages: 'lifecycleStages/getStages',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
      teams: 'teams/getTeams',
    }),
    assignableAgents() {
      return this.$store.getters['inboxAssignableAgents/getAssignableAgents'](
        this.inboxId
      );
    },
    agentMenuSections() {
      const selectedUserId =
        this.assignee?.assignee_type === 'AgentBot' ? null : this.assignee?.id;
      const selectedAiAgentId =
        this.assignee?.assignee_type === 'AgentBot' ? this.assignee?.id : null;

      return buildAgentAssignmentSections({
        humanAgents: this.assignableAgents.filter(
          agent => agent.assignee_type !== 'AgentBot'
        ),
        aiAgents: this.assignableAgents.filter(
          agent => agent.assignee_type === 'AgentBot'
        ),
        teams: this.teams,
        getTeamMembers: teamId =>
          this.$store.getters['teamMembers/getTeamMembers'](teamId) || [],
        selectedUserId,
        selectedAiAgentId,
        includeUnassigned: true,
        unassignedLabel: this.$t('CONVERSATION.UNASSIGNED'),
        unassignedAction: 'assignAgent',
        userAction: 'assignAgent',
        aiAction: 'assignAgent',
        aiAgentsTitle: this.$t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS'),
        aiAgentsBadgeLabel: this.$t('GENERAL.BETA'),
      });
    },
    showSnooze() {
      // Don't show snooze if the conversation is already snoozed/resolved/pending
      return this.status === wootConstants.STATUS_TYPE.OPEN;
    },
  },
  mounted() {
    this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
    this.$store.dispatch('lifecycleStages/get');
    fetchAgentTeamData(this.$store);
  },
  methods: {
    isAllowed(keys) {
      if (!this.allowedOptions.length) return true;
      return keys.some(key => this.allowedOptions.includes(key));
    },
    toggleStatus(status, snoozedUntil) {
      this.$emit('updateConversation', status, snoozedUntil);
    },
    onSelectSnoozeAction(action) {
      if (action.type === 'customDateTime') {
        this.toggleStatus(
          wootConstants.STATUS_TYPE.SNOOZED,
          getUnixTime(action.value)
        );
        this.$emit('close');
        return;
      }

      this.toggleStatus(
        wootConstants.STATUS_TYPE.SNOOZED,
        getUnixTime(action.value)
      );
      this.$emit('close');
    },
    assignPriority(priority) {
      this.$emit('assignPriority', priority);
    },
    updateLifecycleStage(stageId) {
      if (!this.contactId) return;
      this.$store.dispatch('contacts/update', {
        id: this.contactId,
        lifecycle_stage_id: stageId,
      });
      this.$emit('close');
    },
    show(key) {
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    handleAssignAgentSelect(agent) {
      if (agent.value === UNASSIGNED_VALUE) {
        this.$emit('assignAgent', agent.raw);
        this.$emit('close');
        return;
      }

      this.$emit('assignAgent', agent.raw);
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div
    class="min-w-[192px] rounded-2xl border border-n-weak bg-n-surface-1 p-1.5 shadow-xl"
  >
    <template v-if="isAllowed([MENU.STATUS, MENU.SNOOZE])">
      <template v-for="option in statusMenuConfig">
        <MenuItem
          v-if="show(option.key) && isAllowed([MENU.STATUS])"
          :key="option.key"
          :option="option"
          variant="icon"
          @click.stop="toggleStatus(option.key, null)"
        />
      </template>
      <MenuItemWithSubmenu
        v-if="showSnooze && isAllowed([MENU.SNOOZE])"
        :option="snoozeOption"
        submenu-class="max-h-none overflow-visible"
        :submenu-estimated-height="420"
      >
        <SnoozeQuickActionsMenu @select="onSelectSnoozeAction" />
      </MenuItemWithSubmenu>
      <hr class="mx-1.5 my-1 rounded border-b border-n-weak" />
    </template>
    <template
      v-if="isAllowed([MENU.PRIORITY, MENU.LABEL, MENU.AGENT, MENU.LIFECYCLE])"
    >
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.PRIORITY])"
        :option="priorityConfig"
      >
        <MenuItem
          v-for="(option, i) in priorityConfig.options"
          :key="i"
          :option="option"
          @click.stop="assignPriority(option.key)"
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.LABEL])"
        :option="labelMenuConfig"
        :sub-menu-available="!!labels.length"
        submenu-class="max-h-none overflow-visible"
        :submenu-estimated-height="420"
      >
        <LabelQuickActionsMenu
          :labels="labels"
          :selected-labels="conversationLabels"
          @select="
            label =>
              conversationLabels.includes(label.title)
                ? $emit('removeLabel', label)
                : $emit('assignLabel', label)
          "
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.AGENT])"
        :option="agentMenuConfig"
        :sub-menu-available="!!agentMenuSections.length"
        submenu-class="w-[272px] !max-h-none !overflow-visible !p-0 !bg-n-alpha-3 backdrop-blur-[100px]"
        :submenu-estimated-height="360"
      >
        <AgentLoadingPlaceholder v-if="assignableAgentsUiFlags.isFetching" />
        <AgentAssignmentList
          v-else
          :sections="agentMenuSections"
          :thumbnail-size="24"
          :search-placeholder="
            $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
          "
          :autofocus-search="false"
          @select="handleAssignAgentSelect"
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.LIFECYCLE])"
        :option="lifecycleMenuConfig"
        :sub-menu-available="!!lifecycleStages.length"
        submenu-class="min-w-[272px] max-h-none overflow-visible p-0"
        :submenu-estimated-height="320"
      >
        <LifecycleQuickActionsMenu
          :stages="lifecycleStages"
          :selected-stage-id="lifecycleStageId"
          @select="updateLifecycleStage"
        />
      </MenuItemWithSubmenu>
    </template>
  </div>
</template>
